import { supabase } from '../supabaseClient'

// The single person-search query used across every Granite Graph tool that searches
// `deceased` records — field tool (matrix matching, related-person lookup, search mode),
// public Search, and Person Research/PersonView. See Decision 6 (Search Consistency) in
// docs/architecture/decisions.md. Do not re-implement this query elsewhere — import it.
//
// Accepts "First Last" / "First Middle Last" or "Last, First" input. `deathYearVerbatim`,
// when supplied, narrows results to a +/-15 year window around the extracted year (plus
// records with no recorded death date) — used when matching a Gemini-extracted person
// against the database.
// "Last, First" puts the surname before the comma; otherwise the last token is
// treated as the surname (single-token queries have no surname assumption at all).
export function parsePersonName(query) {
  const trimmed = (query || '').trim()
  if (trimmed.includes(',')) {
    const [lnPart, ...rest] = trimmed.split(',').map(s => s.trim()).filter(Boolean)
    return { lastName: lnPart, firstTerms: rest.join(' ').split(/\s+/).filter(Boolean) }
  }
  const tokens = trimmed.split(/\s+/).filter(Boolean)
  if (tokens.length <= 1) return { lastName: null, firstTerms: tokens }
  return { lastName: tokens[tokens.length - 1], firstTerms: tokens.slice(0, -1) }
}

export function buildPersonSearchQuery(query, deathYearVerbatim, limit = 20) {
  const { lastName, firstTerms } = parsePersonName(query)

  let q = supabase.from('v_deceased_search').select('*')
  if (!lastName && firstTerms.length === 1) {
    const t = firstTerms[0]
    q = q.or(`first_name.ilike.*${t}*,last_name.ilike.*${t}*,maiden_name.ilike.*${t}*`)
  } else {
    if (lastName) q = q.ilike('last_name', `%${lastName}%`)
    firstTerms.forEach(term => {
      q = q.or(`first_name.ilike.%${term}%,middle_name.ilike.%${term}%,maiden_name.ilike.%${term}%`)
    })
  }

  const yr = (deathYearVerbatim || '').match(/\d{4}/)?.[0]
  if (yr) {
    const y = parseInt(yr, 10)
    if (y >= 1700 && y <= 2030) {
      q = q.or(`date_of_death.is.null,and(date_of_death.gte.${y - 15}-01-01,date_of_death.lte.${y + 15}-12-31)`)
    }
  }

  return q.order('last_name').order('first_name').limit(limit)
}
